import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';

interface SavingGuard {
  /**
   * Attempt a navigation. While saving, the user is prompted with a leave/stay dialog
   * and `proceed` only runs on confirm; otherwise it runs immediately.
   */
  requestNavigation: (proceed: () => void) => void;
}

/**
 * Guards a page while a save is in progress:
 *  1. Marks navigation as blocked so in-app navigation (sidebar, Back buttons) is routed
 *     through the shared leave/stay dialog instead of navigating immediately.
 *  2. Warns on browser refresh/close/URL-bar navigation via the native `beforeunload` prompt.
 *  3. Intercepts the browser Back button and surfaces the same leave/stay dialog.
 *
 * The save itself is NOT cancelled if the user leaves — they simply won't see the result banner.
 */
export function useSavingGuard(saving: boolean): SavingGuard {
  const { setBlocked, requestNavigation } = useNavigationGuard();
  const leavingRef = useRef(false);
  const mountedRef = useRef(true);

  // Track unmount via a layout effect so its cleanup runs before the passive popstate
  // cleanup below — letting us distinguish "save finished while staying" (pop the guard
  // entry) from "component unmounted due to navigation" (leave history alone).
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // 1. Mark navigation as blocked while saving.
  useEffect(() => {
    setBlocked(saving);
    return () => setBlocked(false);
  }, [saving, setBlocked]);

  // 2. Native browser prompt on refresh / close / external navigation.
  useEffect(() => {
    if (!saving) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    globalThis.addEventListener('beforeunload', handler);
    return () => globalThis.removeEventListener('beforeunload', handler);
  }, [saving]);

  // 3. Trap the browser Back button and route it through the shared leave/stay dialog.
  useEffect(() => {
    if (!saving) return undefined;
    leavingRef.current = false;
    // Push a guard entry so pressing Back fires `popstate` instead of leaving the page.
    globalThis.history.pushState(null, document.title, globalThis.location.href);

    const onPopState = () => {
      if (leavingRef.current) return;
      // Re-push the guard entry to keep the user on the page, then prompt them.
      globalThis.history.pushState(null, document.title, globalThis.location.href);
      requestNavigation(() => {
        leavingRef.current = true;
        // Skip past the guard entry and the current page to the previous history entry.
        globalThis.history.go(-2);
      });
    };

    globalThis.addEventListener('popstate', onPopState);
    return () => {
      globalThis.removeEventListener('popstate', onPopState);
      // Only pop the leftover guard entry when the save finished and the user stayed on the
      // page (still mounted). On unmount from a real navigation, leave history untouched so
      // we don't bounce the user away from their intended destination.
      if (mountedRef.current && !leavingRef.current) {
        globalThis.history.back();
      }
    };
  }, [saving, requestNavigation]);

  return { requestNavigation };
}
